import React, { useRef, useEffect } from 'react';


const COLORS = [
  '#fca5a5', '#fcd34d', '#86efac', '#93c5fd',
  '#d8b4fe', '#f9a8d4', '#fdba74', '#cbd5e1', '#ADFF2F'
];
const TYPE_COLORS = {
  inspection: '#86efac', // green
  service:    '#93c5fd', // blue
  wip:        '#d8b4fe', // purple
  operations: '#ADFF2F', // yellow-green
  OFF:  '#E0DEDE', // dark gray
};
const colorFor = (ev, techId) =>
  TYPE_COLORS[ev.type] || COLORS[Math.abs(techId) % COLORS.length];

const toFloat = (t = '00:00:00') => {
  const [h, m] = t.split(':').map(Number);
  return h + (m || 0) / 60;
};
const overlap = (a1, a2, b1, b2) => a1 < b2 && a2 > b1;

const hourLabel = h => {
  const ampm = h < 12 ? 'AM' : 'PM';
  let h12 = h % 12; if (h12 === 0) h12 = 12;
  return `${h12}:00 ${ampm}`;
};

export default function UnifiedDayTimeline({
  dayDate: selectedDay,
  events = [],
  employees: technicians = [],
  onEventClick,
  onEmptySlot,
}) {

  const timelineRef = useRef(null);
  const hourCols = 24, colWidth = 90, rowHeight = 80, timeColSpan = hourCols * colWidth;
  const techMap = {};


  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = 6 * colWidth;
    }
  }, []);

  technicians.forEach(tech => {
    const techId = String(tech.id);
    techMap[techId] = events.filter(ev => {
      // Normalization
      const leadId = String(ev.lead_tech_id || ev.tech_id || ev.TECH || '');
      const addlIds = [];

      if (ev.additional_tech_id) {
        addlIds.push(String(ev.additional_tech_id));
      }
      // Support array form (service may have add_tech array)
      if (Array.isArray(ev.add_tech)) {
        ev.add_tech.forEach(t => {
          if (t && t.tech_id) addlIds.push(String(t.tech_id));
        });
      }
      return (
        eventMatchesDay(ev, selectedDay) &&
        (leadId === techId || addlIds.includes(techId))
      );
    });
  });

  function isSameOrBetween(day, startDate, endDate) {
    // Extract Y/M/D local for each date
    const getYMD = d => [d.getFullYear(), d.getMonth(), d.getDate()];
    const dArr = getYMD(day);
    const sArr = getYMD(startDate);
    const eArr = getYMD(endDate);

    // Compare as arrays (Y,M,D)
    const compare = (a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
    return compare(dArr, sArr) >= 0 && compare(dArr, eArr) <= 0;
  }

  function eventMatchesDay(ev, selectedDay) {
    const parseSafe = s => {
      // Parse as local if possible (YYYY-MM-DD → new Date(Y, M-1, D))
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [y,m,d] = s.split('-').map(Number);
        return new Date(y, m-1, d);
      }
      return new Date(s);
    };
    const startDate = parseSafe(ev.scheduled_date || ev.scheduled_due_date || ev.schedule_from || ev.SCHEDULE_DATE || ev.date_from || ev.date || ev.date_out);
    const endDate = parseSafe(
      ev.schedule_date_to ||
      ev.schedule_to ||
      ev.date_to ||
      ev.scheduled_date ||
      ev.scheduled_due_date ||
      ev.SCHEDULE_DATE ||
      ev.date ||
      ev.date_end ||
      startDate
    );
    return isSameOrBetween(selectedDay, startDate, endDate);
  }
 
const renderBlocks = (evList, techId) => evList.map((ev, idx) => {
  const start = toFloat(ev.start_time || '00:00:00');
  const end = toFloat(ev.end_time || '23:59:59');
  const left = start * colWidth;
  const width = Math.max((end - start) * colWidth, colWidth / 2);

  const siblings = evList.filter(o =>
    overlap(start, end, toFloat(o.start_time), toFloat(o.end_time))
  ).sort((a, b) => toFloat(a.start_time) - toFloat(b.start_time));
  const index = siblings.indexOf(ev);
  const sibCount = siblings.length;
  const sibWidth = width / sibCount;
  const sibLeft = left + index * sibWidth;

  // Default background
  let bg = colorFor(ev, techId);
  let extraStyles = {};
  let extraClass = "";

  // Tentative highlight
  if (
    String(ev.status).toLowerCase() === 'tentative' ||
    String(ev.inspection_status).toLowerCase() === 'tentative' ||
    String(ev.STATUS).toLowerCase() === 'tentative'
  ) {
    bg = '#fdba74'; // Tailwind orange-300
    extraStyles = { fontWeight: 'bold' };
    extraClass = 'bg-orange-300 font-bold';
  }

  // FDNY flag in notes
  const notesText = (ev.notes || ev.user_comment || ev.tech_notes || '').toLowerCase();
  if (notesText.includes('@fdny')) {
    bg = '#fca5a5'; // Tailwind red-300
    extraStyles = { fontWeight: 'bold' };
    extraClass = 'bg-red-300 font-bold';
  }

  return (
    <div
      key={idx}
      onClick={e => { e.stopPropagation(); onEventClick(ev); }}
      className={extraClass}
      style={{
        position: 'absolute', top: 4, bottom: 4,
        left: sibLeft, width: sibWidth - 4,
        background: bg,
        borderRadius: 4, padding: 4,
        overflow: 'hidden', fontSize: 11, cursor: 'pointer',
        ...extraStyles
      }}
    >
      {/* COMPLETED ribbon */}
      {String(ev.status) === 'Completed' && (
        <span style={{
          position: 'absolute', top: 62, right: 0,
          width: 110, textAlign: 'center',
          background: '#16a34a', color: '#fff',
          fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px',
          padding: '2px 0', transform: 'rotate(45deg)', transformOrigin: 'top right',
          boxShadow: '0 1px 3px rgba(0,0,0,.35)', zIndex: 9,
        }}>
          COMPLETED
        </span>
      )}

      {/* Overlap icon */}
      {sibCount > 1 && (
        <span
          title="Overlapping event"
          style={{
            position: 'absolute', top: 4, right: 4,
            fontSize: 15,
          }}
        >
          🛑
        </span>
      )}

      <span style={{ fontWeight: 600, fontSize: 10, color: '#555' }}>{ev.type?.toUpperCase() || ''}</span><br />
      {ev.type?.toUpperCase() !== 'OPERATIONS' ? (
        <>
          <strong>{ev.job_site || ev.job_site_address || 'No site'}</strong><br />
          <span>{ev.customer || ev.customer_name || 'No cust'}</span><br />
        </>
      ) : (
        <><strong className="truncate">{ev.description || '-'}</strong><br /></>
      )}
      <span>
        {ev.start_time?.slice(0, 5)}-{ev.end_time?.slice(0, 5)}
      </span>
    </div>
  );
});


  // --- Header Row ---
  const header = (
    <div
      style={{
        position: 'sticky', top: 0, zIndex: 10,
        display: 'grid',
        gridTemplateColumns: `200px ${timeColSpan}px`,
        borderBottom: '2px solid #e5e7eb',
        fontWeight: 600,
        background: '#f9fafb'
      }}
    >
      <div style={{ background: '#f3f4f6', position: 'sticky', left: 0, zIndex: 31 }} />
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${hourCols}, ${colWidth}px)` }}>
        {Array.from({ length: hourCols }).map((_, h) => (
          <div key={h} style={{
            textAlign: 'center', borderLeft: '1px solid #e5e7eb',
            background: '#f9fafb'
          }}>
            {hourLabel(h)}
          </div>
        ))}
      </div>
    </div>
  );

  // --- Rows ---
  const rows = technicians.map((tech, rIdx) => {
    const techId = tech.id ?? 'unknown';
    const list = techMap[techId] || [];
    return (
      <div key={techId} style={{
        display: 'grid',
        gridTemplateColumns: `200px ${timeColSpan}px`,
        borderBottom: '1px solid #e5e7eb',
        height: rowHeight, position: 'relative'
      }}
        onClick={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left - 200; // after frozen col
          if (x < 0) return; // clicked on name column
          const hourFloat = x / colWidth;
          const hour = Math.floor(hourFloat);
          const minute = Math.round((hourFloat - hour) * 60 / 15) * 15;
          onEmptySlot(techId, hour, minute); // <<< FIXED
        }}
      >
        {/* 1) frozen tech name column */}
        <div style={{
          background: '#fafafa', padding: '4px 6px',
          borderRight: '1px solid #e5e7eb',
          whiteSpace: 'nowrap', overflow: 'hidden',
          position: 'sticky', left: 0, zIndex: 20
        }}>
          <span className="font-semibold">{tech.first_name} {tech.last_name}</span>
          {tech.title && (
          <div className={`text-xs ${tech.title.toLowerCase().includes('helper') ? 'text-yellow-500' 
                                                                                  : tech.o_f_type === 'Field' 
                                                                                  ? 'text-blue-600' 
                                                                                  : 'text-gray-600'}`}>{tech.title}
          </div>
          )}
          <div className="text-xs text-gray-500">{tech.department}</div>
        </div>
        {/* 2) timeline grid cell */}
        <div style={{ position: 'relative' }}>
          {/* hour vertical lines */}
          {Array.from({ length: hourCols }).map((_, h) => (
            <div key={h} style={{
              position: 'absolute', top: 0, bottom: 0,
              left: h * colWidth, width: 0,
              borderLeft: '1px solid #eee'
            }} />
          ))}
          {renderBlocks(list, techId)}
        </div>
      </div>
    );
  });

  // --- Final Render ---
  return (
    <div className="border rounded overflow-x-auto">
      {/* day title */}
      <div className="text-center py-2 font-bold">
        {selectedDay
          ? selectedDay.toLocaleDateString('en-US', {
            weekday: 'long', month: 'short', day: 'numeric'
          })
          : 'No date selected'
        }
      </div>
        <div ref={timelineRef}
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            position: 'relative'
          }}
        >
        {header}
        {rows}
      </div>
    </div>
  );
}
